// Single source of truth for the dock nav, shared by every page.
// Each page includes `<div id="dock-root"></div>` and calls
// mountDock("<page-id>") instead of hand-copying the dock markup —
// fixes the old copy/paste bugs (dead Home link, hardcoded active state).
import { initDockHover } from "./dock.js";
import { mountCartDrawer } from "./cartDrawer.js";

// ── Boot screen ──
// Fake terminal boot log, played on the first page load of a session only
// (bootScreenEarly.js injects the empty #boot-screen element before this
// ever runs, and decides boot-screen vs. skeleton-screen — see below; if
// neither exists, e.g. prefers-reduced-motion, initLoadScreen() is a
// no-op). A fixed header and closing line bookend a handful of lines
// picked at random each time, each typed out character by character with
// a real wall-clock timestamp, like an actual boot log rather than a
// canned animation. Then the wordmark stamps in huge and red, and the
// whole thing fades and removes itself.
const HEADER_LINE = "JABUILDS BIOS v2.6";
const CLOSING_LINE = "LOADING GOLDFISHOS...";
const RANDOM_LINES = [
    "CHECKING RAM.......... OK",
    "DETECTING GPU.......... OK",
    "SPINNING UP COOLING FANS.......... OK",
    "LOCATING GOLDFISH.......... FOUND",
    "CALIBRATING RGB.......... OK",
    "COUNTING SCREWS.......... 47",
    "CABLE MANAGEMENT.......... ATTEMPTED",
    "SCANNING FOR DUST BUNNIES.......... NONE",
    "CONSULTING THE MANUAL.......... SKIPPED",
    "CROSSING FINGERS.......... OK",
    "CHECKING WIFI SIGNAL.......... STRONG",
    "SCANNING FOR BLUE SCREENS.......... NOT FOUND",
    "WAKING UP THE GOLDFISH.......... OK",
    "APPLYING THERMAL PASTE.......... OK",
    "TIGHTENING LAST SCREW.......... OK",
];
const RANDOM_LINE_COUNT = 3;
// Slow enough to actually read as it types, not just a flicker.
const CHAR_DELAY_MS = 32;
// Short — the cursor keeps blinking through this gap (see typeLine), so
// lines read as one continuous stream instead of stopping dead each time.
const LINE_PAUSE_MS = 160;
// However long the page actually takes to load (fonts/images/etc.) plus
// this fixed pad, so the screen never feels rushed even on a fast load —
// see waitForPageLoad() below.
const EXTRA_HOLD_MS = 2000;

function pickRandomLines() {
    const pool = [...RANDOM_LINES];
    const picked = [];
    for (let n = 0; n < RANDOM_LINE_COUNT && pool.length; n += 1) {
        const index = Math.floor(Math.random() * pool.length);
        picked.push(pool.splice(index, 1)[0]);
    }
    return picked;
}

// The actual current wall-clock time (e.g. "1:37:27.412"), not elapsed
// time since the animation started — reads like a real system log line.
function formatNow() {
    const d = new Date();
    let hours = d.getHours() % 12;
    if (hours === 0) hours = 12;
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const seconds = String(d.getSeconds()).padStart(2, "0");
    const ms = String(d.getMilliseconds()).padStart(3, "0");
    return `${hours}:${minutes}:${seconds}.${ms}`;
}

// Resolves once the page (images, fonts, video, etc.) has actually
// finished loading — resolves immediately if that already happened
// before this ran.
function waitForPageLoad() {
    if (document.readyState === "complete") return Promise.resolve();
    return new Promise((resolve) =>
        window.addEventListener("load", resolve, { once: true }),
    );
}

// Types `text` into a new line inside `container`, character by character.
// `cursor` is one shared element reused across every line (moved here as
// typing starts) so it keeps blinking continuously through the pause
// between lines instead of disappearing and reappearing — the whole log
// reads as one continuous flow rather than separate stop-start lines.
// Stops early once `isDone()` returns true (the visitor skipped).
function typeLine(container, text, cursor, isDone) {
    return new Promise((resolve) => {
        const line = document.createElement("div");
        line.className = "boot-screen__line";
        const textEl = document.createElement("span");
        line.appendChild(textEl);
        line.appendChild(cursor);
        container.appendChild(line);

        let i = 0;
        function step() {
            if (i < text.length && !isDone()) {
                textEl.textContent += text[i];
                i += 1;
                setTimeout(step, CHAR_DELAY_MS);
            } else {
                resolve();
            }
        }
        step();
    });
}

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

async function initBootScreen(el) {
    const lines = document.createElement("pre");
    lines.className = "boot-screen__lines";
    el.appendChild(lines);

    const logo = document.createElement("div");
    logo.className = "boot-screen__logo";
    logo.textContent = "JABuilds";
    el.appendChild(logo);

    const skipHint = document.createElement("div");
    skipHint.className = "boot-screen__skip";
    skipHint.textContent = "Press any key to skip";
    el.appendChild(skipHint);

    const cursor = document.createElement("span");
    cursor.className = "boot-screen__cursor";

    // The overlay is aria-hidden, so make the page underneath unreachable
    // too — otherwise keyboard focus wanders into content nobody can see.
    const blocked = [
        ...document.querySelectorAll("body > :not(#boot-screen):not(script)"),
    ];
    blocked.forEach((node) => (node.inert = true));

    let done = false;
    const isDone = () => done;
    function finish() {
        if (done) return;
        done = true;
        el.removeEventListener("click", finish);
        document.removeEventListener("keydown", finish);
        blocked.forEach((node) => (node.inert = false));
        el.classList.add("boot-screen--hidden");
        setTimeout(() => el.remove(), 400);
    }
    el.addEventListener("click", finish);
    document.addEventListener("keydown", finish);

    const log = [HEADER_LINE, ...pickRandomLines(), CLOSING_LINE];
    for (const text of log) {
        if (done) return;
        await typeLine(lines, `[${formatNow()}] ${text}`, cursor, isDone);
        await wait(LINE_PAUSE_MS);
    }
    cursor.remove();

    // Don't reveal the logo until the real page is actually ready, plus a
    // fixed pad on top — so it never gets cut short by a fast load, and
    // never drags on forever waiting on a slow one.
    await waitForPageLoad();
    await wait(EXTRA_HOLD_MS);
    if (done) return;

    skipHint.remove();
    logo.classList.add("boot-screen__logo--in");
    await wait(350);
    finish();
}

// ── Skeleton screen ──
// Lightweight placeholder for every page load *after* the first one in a
// session (the first load always gets the full BIOS boot screen instead —
// see bootScreenEarly.js). Unlike the boot screen, this is a genuine
// loading indicator, not a fixed intro, so it only shows up if loading is
// actually slow enough to need one: nothing is created or shown until
// SKELETON_REVEAL_DELAY_MS elapses with the page still not ready. A fast
// load never sees a skeleton at all — no flash, no unnecessary DOM work.
// Shaped roughly like whatever that page actually shows (a title + card
// row for Home, a title + two grids for Shop, etc.), and only ever held
// for as long as the real page takes to finish loading.
const SKELETON_REVEAL_DELAY_MS = 250;

function skeletonBlock(...classNames) {
    const el = document.createElement("div");
    el.className = ["skeleton-block", ...classNames].join(" ");
    return el;
}

function skeletonTitleGroup(el) {
    el.appendChild(skeletonBlock("skeleton-block--title"));
    el.appendChild(skeletonBlock("skeleton-block--line"));
    el.appendChild(
        skeletonBlock("skeleton-block--line", "skeleton-block--short"),
    );
}

function skeletonRow(el, count) {
    const grid = document.createElement("div");
    grid.className = "skeleton-grid";
    for (let i = 0; i < count; i += 1) {
        grid.appendChild(skeletonBlock("skeleton-block--card"));
    }
    el.appendChild(grid);
}

// One layout per page, roughly matching what that page actually contains —
// see mountDock()'s activePageId for the same page-id convention.
const SKELETON_LAYOUTS = {
    // Hero + the 3-card services row.
    home(el) {
        skeletonTitleGroup(el);
        skeletonRow(el, 3);
    },
    // Hero + the 4-step process grid + the tiers grid.
    shop(el) {
        skeletonTitleGroup(el);
        skeletonRow(el, 4);
        skeletonRow(el, 3);
    },
    // Title + mission/story copy + the 2-up team grid.
    about(el) {
        skeletonTitleGroup(el);
        skeletonRow(el, 2);
    },
    // Title + the large Cal.com embed.
    contact(el) {
        skeletonTitleGroup(el);
        el.appendChild(skeletonBlock("skeleton-block--tall"));
    },
    // Title + the photo grid.
    gallery(el) {
        skeletonTitleGroup(el);
        skeletonRow(el, 4);
    },
};

async function initSkeletonScreen(pageId) {
    if (
        window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
        return;
    }

    let loaded = false;
    const loadPromise = waitForPageLoad().then(() => {
        loaded = true;
    });

    // Race the real load against the reveal delay. If loading wins, the
    // page was fast enough that a skeleton would just be a flash — skip
    // it entirely and never touch the DOM.
    await Promise.race([
        loadPromise,
        new Promise((resolve) => setTimeout(resolve, SKELETON_REVEAL_DELAY_MS)),
    ]);
    if (loaded) return;

    const el = document.createElement("div");
    el.id = "skeleton-screen";
    el.className = "skeleton-screen";
    el.setAttribute("aria-hidden", "true");
    document.body.appendChild(el);

    const buildLayout = SKELETON_LAYOUTS[pageId] || SKELETON_LAYOUTS.home;
    buildLayout(el);

    // Force layout so the opacity transition actually animates instead of
    // starting already-visible.
    requestAnimationFrame(() => el.classList.add("skeleton-screen--visible"));

    await loadPromise;

    el.classList.remove("skeleton-screen--visible");
    setTimeout(() => el.remove(), 300);
}

// Called by every page's entry script with the same page id it already
// passes to mountDock(). Boot screen: bootScreenEarly.js already injected
// #boot-screen synchronously (first load of the session only) — find it
// and bring it to life. Otherwise, let initSkeletonScreen() decide for
// itself whether this load is slow enough to bother with.
export function initLoadScreen(pageId) {
    const boot = document.getElementById("boot-screen");
    if (boot) return initBootScreen(boot);
    return initSkeletonScreen(pageId);
}

// Icon paths must be resolved with `new URL(..., import.meta.url)` (Parcel's
// documented pattern for referencing static assets from JS) so Parcel adds
// them to the asset graph and rewrites them to hashed build URLs — a bare
// string path here would 404/fall through in the built site.
const homeIcon = new URL("../img/Home.png", import.meta.url).href;
const shopIcon = new URL("../img/Store.png", import.meta.url).href;
const aboutIcon = new URL("../img/AboutUs.png", import.meta.url).href;
const contactIcon = new URL("../img/Contact.svg", import.meta.url).href;
const cartIcon = new URL("../img/Cart.svg", import.meta.url).href;
const ICONS = [
    {
        id: "home",
        href: "./index.html",
        icon: homeIcon,
        label: "Home",
    },
    {
        id: "shop",
        href: "./shop.html",
        icon: shopIcon,
        label: "Shop",
    },
    {
        id: "about",
        href: "./aboutus.html",
        icon: aboutIcon,
        label: "About Us",
    },
    {
        id: "contact",
        href: "./contact.html",
        // PLACEHOLDER icon (src/img/Contact.svg) — swap for real matching-style art
        icon: contactIcon,
        label: "Contact",
    },
    {
        id: "cart",
        // PLACEHOLDER icon (src/img/Cart.svg) — swap for real matching-style art
        icon: cartIcon,
        label: "Cart",
        action: "cart",
    },
];

function buildIconLink(item, activePageId) {
    const a = document.createElement("a");
    a.className = "nav-item__link";
    a.href = item.href;
    if (item.id === activePageId) a.setAttribute("aria-current", "page");

    const img = document.createElement("img");
    img.src = item.icon;
    img.alt = item.label;
    img.width = 52;
    img.height = 52;
    img.className = "image";
    a.appendChild(img);
    return a;
}

// The Cart icon triggers a drawer instead of navigating, so it's a
// <button> that dispatches "cart:open" rather than an <a href>.
function buildIconButton(item) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "nav-item__link nav-item__link--button";
    // Kept in sync with the item count by cartDrawer.js's render().
    button.setAttribute("aria-label", "Cart");
    button.setAttribute("aria-haspopup", "dialog");
    button.addEventListener("click", () => {
        window.dispatchEvent(new CustomEvent("cart:open"));
    });

    const img = document.createElement("img");
    img.src = item.icon;
    img.alt = "";
    img.width = 52;
    img.height = 52;
    img.className = "image";
    button.appendChild(img);

    const badge = document.createElement("span");
    badge.className = "nav-item__badge";
    badge.hidden = true;
    button.appendChild(badge);

    return button;
}

function buildNavItem(item, activePageId) {
    const li = document.createElement("li");
    li.className = "nav-item" + (item.id === activePageId ? " active" : "");
    li.appendChild(
        item.action === "cart"
            ? buildIconButton(item)
            : buildIconLink(item, activePageId),
    );

    const tooltip = document.createElement("div");
    tooltip.className = "nav-item__tooltip";
    tooltip.setAttribute("aria-hidden", "true");
    tooltip.textContent = item.label;
    li.appendChild(tooltip);

    const dot = document.createElement("span");
    dot.className = "nav-item__dot";
    li.appendChild(dot);

    return li;
}

/**
 * Render the dock into #dock-root and mark `activePageId`'s icon active.
 * @param {string} activePageId - one of the ids in ICONS (e.g. "home")
 */
export function mountDock(activePageId) {
    const root = document.getElementById("dock-root");
    if (!root) return;

    const navWrap = document.createElement("div");
    navWrap.className = "nav-wrap";

    const navBar = document.createElement("nav");
    navBar.className = "nav-bar";
    navBar.setAttribute("aria-label", "Main");

    const navList = document.createElement("ul");
    navList.className = "nav-list";

    ICONS.forEach((item) => {
        navList.appendChild(buildNavItem(item, activePageId));
    });

    navBar.appendChild(navList);
    navWrap.appendChild(navBar);

    // Pages without their own dock icon (e.g. Gallery) fall back to their
    // id, title-cased, instead of showing nothing.
    const activeItem = ICONS.find((item) => item.id === activePageId);
    const currentPageLabel =
        activeItem?.label ??
        (activePageId
            ? activePageId.charAt(0).toUpperCase() + activePageId.slice(1)
            : "");
    if (currentPageLabel) {
        const currentPage = document.createElement("div");
        currentPage.className = "nav-current-page";
        currentPage.setAttribute("aria-hidden", "true");
        currentPage.textContent = currentPageLabel;
        navWrap.appendChild(currentPage);
    }

    root.replaceChildren(navWrap);

    initDockHover(navWrap);
    mountCartDrawer();
}

const FOOTER_LINKS = [
    { label: "Builds", href: "./shop.html" },
    { label: "Gallery", href: "./gallery.html" },
    { label: "How it works", href: "./shop.html#process" },
    { label: "Pricing", href: "./shop.html#pricing" },
    { label: "Contact", href: "./contact.html" },
];

/**
 * Render the shared footer into #footer-root. Every page gets the same
 * secondary-page links (Gallery, Services, Pricing) instead of needing
 * its own dock slot for content that isn't top-level navigation.
 */
export function mountFooter() {
    const root = document.getElementById("footer-root");
    if (!root) return;

    const footer = document.createElement("footer");
    footer.className = "site-footer";

    const nav = document.createElement("nav");
    nav.className = "site-footer__links";
    nav.setAttribute("aria-label", "Footer");
    FOOTER_LINKS.forEach((link) => {
        const a = document.createElement("a");
        a.href = link.href;
        a.textContent = link.label;
        nav.appendChild(a);
    });
    footer.appendChild(nav);

    const copyright = document.createElement("p");
    copyright.className = "site-footer__copyright";
    copyright.textContent = `© ${new Date().getFullYear()} JABuilds, New York City`;
    footer.appendChild(copyright);

    root.replaceChildren(footer);
}
